// Transport for RUM batches. Telemetry is BEST-EFFORT and must never throw into
// the app or block navigation (doctrine 1.5 — degrade gracefully in UX).
//
//  - `beacon` (web/admin, same-origin): `navigator.sendBeacon("/api/rum", blob)`.
//    The browser flushes it even as the page unloads and it rides the guest
//    cookie automatically. A relative path keeps it same-origin.
//  - `fetch` (seller, cross-origin CDN): `fetch(absoluteUrl, { keepalive: true,
//    credentials: "omit" })` — the CDN origin can't send a same-origin beacon, so
//    it POSTs cross-origin with no credentials (backend CORS-allowlists the origin).
//
// If a beacon is refused (the ~64KB user-agent queue is full) we fall back to a
// keepalive fetch so a full queue never silently drops the batch.

import type { ClientErrorBatchRequest, RumBatchRequest } from "./types";
import type { ClientFunnelEventBatchRequest } from "../analytics/types";

export type Transport = "beacon" | "fetch";

export function sendBatch(
  path: string,
  body: RumBatchRequest | ClientErrorBatchRequest | ClientFunnelEventBatchRequest,
  transport: Transport,
  endpoint: string,
): void {
  const json = JSON.stringify(body);

  if (
    transport === "beacon" &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    // sendBeacon can throw (e.g. some privacy modes) — never let it reach the app.
    try {
      const queued = navigator.sendBeacon(path, new Blob([json], { type: "application/json" }));
      if (queued) return;
    } catch {
      /* fall through to fetch */
    }
  }

  // fetch path: absolute URL for the cross-origin `fetch` transport; relative for a
  // beacon fallback (which stays same-origin).
  const url = transport === "fetch" ? `${endpoint}${path}` : path;
  try {
    void fetch(url, {
      method: "POST",
      keepalive: true,
      credentials: transport === "fetch" ? "omit" : "same-origin",
      headers: { "content-type": "application/json" },
      body: json,
    }).catch(() => undefined);
  } catch {
    /* best-effort telemetry — swallow so a failed send never breaks the page */
  }
}
